import React from 'react';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { useRefreshBalanceSheetSidebar } from 'Hooks/useRefreshEvent';
import { useSnackbarContext } from 'Context/SnackbarContext';

const BalanceTransferRolloverContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const BAL_TRAN_REFRESH_STARTED = 'bal-tran-started';
const BAL_TRAN_REFRESH_SUCCEEDED = 'bal-tran-succeeded';
const BAL_TRAN_REFRESH_FAILED = 'bal-tran-refresh-failed';


const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    // SAC
    balanceTransferCollection: null,
    isBalTranRefreshing: false,
    balTranRefreshError: null,
    // SAC Groups
    balTransCollection: null,
    isOptimisticallyUpdating: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        balanceTransferCollection: action.balanceTransferCollection,
        balTransCollection: action.balTransCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case BAL_TRAN_REFRESH_STARTED: {
      return {
        ...state,
        isBalTranRefreshing: true,
        balTranRefreshError: null,
      };
    }
    case BAL_TRAN_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isBalTranRefreshing: false,
        balTranRefreshError: null,
        balanceTransferCollection: action.balanceTransferCollection,
        balTransCollection: action.balTransCollection,
      };
    }
    case BAL_TRAN_REFRESH_FAILED: {
      return {
        ...state,
        isBalTranRefreshing: false,
        balTranRefreshError: action.balTranRefreshError,
      };
    }
    default: {
      throw new Error(`Unhandled BalanceTransferContext action type: ${action.type}`);
    }
  }
};

const BalanceTransferRolloverProvider = ({
  relationship,
  children,
}) => {
  const { pageContentResource } = usePageContentContext();
  const { loadModalCollectionByLink } = useModalContext();

  const { displayErrorMessage } = useSnackbarContext();

  const [state, dispatch] = React.useReducer(reducer, null, init);

  const refreshBalTranCollection = React.useCallback(async () => {
    const { balanceTransferCollection: balTranColl } = state;

    if (balTranColl === null) {
      return;
    }

    if (balTranColl.resources().length() === 0) {
      return;
    }

    const [balTranRes] = balTranColl;

    try {
      dispatch({ type: BAL_TRAN_REFRESH_STARTED });
      const coll = await balTranRes.link('self').get();

      dispatch({
        type: BAL_TRAN_REFRESH_SUCCEEDED,
        balanceTransferCollection: coll,
      });
    } catch (err) {
      dispatch({ type: BAL_TRAN_REFRESH_FAILED, balTranRefreshError: err });
    }
  }, [state]);

  const loadCollection = async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      const resp = await relationship.link('related').get();

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        balanceTransferCollection: resp,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  };

  useComponentDidMount(loadCollection);

  const handleAddBalanceTransfer = React.useCallback(
    resource => {
      loadModalCollectionByLink(resource.relationship(fr.relationships.addBalanceTransfer.name).link('related').url());
    },
    [state],
  );

  const handleModifyBalanceTransfer = React.useCallback(
    balanceTransferItem => {
      const selfLink = balanceTransferItem.link('self');
      if (selfLink) {
        const balanceTransferSelfLink = selfLink.url();
        loadModalCollectionByLink(balanceTransferSelfLink);
      }
    },
    [state],
  );


  const handleAddRefinanceDebt = React.useCallback(
    resource => {
      loadModalCollectionByLink(resource.relationship(fr.relationships.addRefinanceDebt.name).link('related').url());
    },
    [state],
  );

  const handleModifyRefinanceDebt = React.useCallback(
    refinanceDebtItem => {
      const selfLink = refinanceDebtItem.link('self');
      if (selfLink) {
        const refinanceDebtSelfLink = selfLink.url();
        loadModalCollectionByLink(refinanceDebtSelfLink);
      }
    },
    [state],
  );

  useRefreshBalanceSheetSidebar(async () => {
    await loadCollection();
  }, []);

  // Refresh the groups whenever the page is loading
  React.useEffect(() => {
    refreshBalTranCollection().catch(err => {
      displayErrorMessage(err, 'Failed to refresh money movement collection');
    });
  }, [pageContentResource]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      balanceTransferCollection: state.balanceTransferCollection,
      balanceTransferResource: state.balanceTransferCollection?.resources().head(), // collection is null to start
      isBalTranRefreshing: state.isBalTranRefreshing,
      balTranRefreshError: state.balTranRefreshError,
      isOptimisticallyUpdating: state.isOptimisticallyUpdating,
      refreshBalTranCollection,
      handleAddBalanceTransfer,
      handleModifyBalanceTransfer,
      handleAddRefinanceDebt,
      handleModifyRefinanceDebt,
    };
  }, [
    state,
    refreshBalTranCollection,
  ]);

  return (
    <BalanceTransferRolloverContext.Provider value={memoizedValue}>
      {children}
    </BalanceTransferRolloverContext.Provider>
  );
};

// Use BalanceTransferRollover State Context
const useBalanceTransferRolloverContext = () => {
  const context = React.useContext(BalanceTransferRolloverContext);
  if (context === undefined) {
    throw new Error('useBalanceTransferContext must be used within a BalanceTransferProvider');
  }
  return context;
};

BalanceTransferRolloverProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  relationship: PropTypes.object.isRequired,
};

export {
  BalanceTransferRolloverProvider,
  useBalanceTransferRolloverContext,
};
